import React, { useState } from "react";
import { X, Lock, CheckCircle, Clock, Utensils, BookOpen, MessageCircle } from "lucide-react";
import { LuLeaf } from "react-icons/lu";
import { LuShieldCheck } from "react-icons/lu";

const PlanPaymentPopup = ({ open, onClose, program, variant, onProceed, formatPrice, getPriceWithGST }) => {
    const [isProcessing, setIsProcessing] = useState(false);

    if (!open) return null;

    const programName = program?.name;
    const programDescription = program?.description;

    const currentVariant = variant;
    const price = currentVariant ? parseFloat(currentVariant.price) : 9999;
    const gstPercentage = currentVariant?.gst_percentage || 18;
    const totalPrice = getPriceWithGST ? getPriceWithGST(price, gstPercentage) : price + (price * gstPercentage / 100);
    const gstAmount = totalPrice - price;

    // Get duration display name from variant
    const getDurationDisplay = (variant) => {
        if (!variant) return "Select Duration";
        return variant.duration || "Duration";
    };

    // Get discount percentage based on duration
    const getDiscount = (duration) => {
        if (!duration) return null;
        const durationLower = duration.toLowerCase();
        if (durationLower.includes('2 month')) return '7%';
        if (durationLower.includes('3 month')) return '20%';
        if (durationLower.includes('6 month')) return '25%';
        return null;
    };

    const handleProceed = () => {
        setIsProcessing(true);
        if (onProceed) {
            onProceed();
        }
        setTimeout(() => setIsProcessing(false), 1000);
    };

    // Get all variants for the program
    const allVariants = program?.ProgramVariants || [];

    return (
        <div className="fixed inset-0 font-lato bg-black/40 flex items-center justify-center z-50 px-4">
            <div className="
                w-full max-w-[650px]
                bg-white rounded-md shadow-xl relative
                max-h-[90vh] overflow-y-auto
                [&::-webkit-scrollbar]:hidden
                [-ms-overflow-style:none]
                [scrollbar-width:none]
            ">
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 bg-gray-100 rounded-full p-1 text-gray-500 hover:bg-gray-200 transition-colors"
                >
                    <X size={18} />
                </button>

                <div className="p-5 pr-8">
                    {/* <span className="text-[12px] bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold flex items-center gap-1 w-fit">
                        <LuLeaf size={14} />
                        NUTRITION PROGRAM
                    </span> */}

                    <h2 className="text-[20px] font-extrabold text-gray-800 mt-3">
                        {programName}
                    </h2>

                    <p className="text-md text-gray-500 mt-1 leading-relaxed">
                        {programDescription}
                    </p>
                </div>

                <hr />

                {/* Duration Selection - Only show selected duration as active, others blocked */}
                <div className="p-5">
                    <p className="text-sm text-gray-500 mb-3 font-medium">
                        SELECTED DURATION
                    </p>

                    <div className="grid grid-cols-3 gap-3">
                        {allVariants.map((variantItem) => {
                            const isSelected = currentVariant?.id === variantItem.id;
                            const discount = getDiscount(variantItem.duration);
                            
                            return (
                                <div
                                    key={variantItem.id}
                                    className={`border-2 rounded-md py-2 text-sm transition-all ${
                                        isSelected 
                                            ? 'border-[#0f977b] bg-[#0f977b]/10 text-[#0f977b] font-semibold cursor-default' 
                                            : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                                    }`}
                                >
                                    <div className="text-center">
                                        {variantItem.duration}
                                        {/* {discount && isSelected && (
                                            <span className="block text-[10px] text-orange-500">
                                                Save {discount}
                                            </span>
                                        )} */}
                                       
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Price Section */}
                    <div className="mt-5 border rounded-lg p-4 flex justify-between items-center bg-gray-50">
                        <div>
                            {currentVariant && (
                                <div className="text-2xl font-bold text-gray-900">
                                    ₹{totalPrice.toFixed(0)}
                                </div>
                            )}
                            <p className="text-[11px] text-gray-400 mt-1">
                                {currentVariant ? (
                                    <>
                                        Incl GST ₹{gstAmount.toFixed(0)} • {currentVariant.duration} plan
                                    </>
                                ) : (
                                    "Please select a duration"
                                )}
                            </p>
                        </div>

                        {/* {currentVariant && getDiscount(currentVariant.duration) && (
                            <span className="bg-orange-100 text-orange-600 text-[12px] px-3 py-1 rounded-full">
                                {getDiscount(currentVariant.duration)} OFF
                            </span>
                        )} */}
                    </div>

                    {/* What's Included */}
                    <p className="text-sm text-gray-500 mt-5 mb-3 font-medium">
                        WHAT'S INCLUDED
                    </p>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex gap-2 items-center">
                            <Clock size={14} className="text-green-600" />
                            <span className="text-sm">Consultation Sessions</span>
                        </div>

                        <div className="flex gap-2 items-center">
                            <Utensils size={14} className="text-green-600" />
                            <span className="text-sm">Personalized Meal Plans</span>
                        </div>

                        <div className="flex gap-2 items-center">
                            <CheckCircle size={14} className="text-gray-500" />
                            <span className="text-sm">Progress Tracking</span>
                        </div>

                        <div className="flex gap-2 items-center">
                            <MessageCircle size={14} className="text-orange-500" />
                            <span className="text-sm">WhatsApp Support</span>
                        </div>

                        <div className="flex gap-2 items-center">
                            <BookOpen size={14} className="text-gray-500" />
                            <span className="text-sm">Diet Resource Library</span>
                        </div>
                    </div>

                    {/* Proceed Button */}
                    <button
                        onClick={handleProceed}
                        disabled={isProcessing || !currentVariant}
                        className="mt-6 w-full bg-[#16a34a] text-white py-3 rounded-md text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#16a34a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isProcessing ? (
                            <>
                                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                                Processing...
                            </>
                        ) : (
                            <>
                                <Lock size={14} />
                                Proceed to Payment · ₹{totalPrice.toFixed(0)}
                            </>
                        )}
                    </button>

                    <p className="text-center text-[14px] text-gray-400 mt-4 flex items-center justify-center gap-1">
                        <LuShieldCheck size={13} className="text-green-500" />
                        Secure Payment
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PlanPaymentPopup;